"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, Clock, MapPin, Sparkles } from "lucide-react";
import type { AppLocale } from "@/core/i18n/config";
import { localizeHref } from "@/core/i18n/routing";
import { getPublicHealerProfilePath } from "@/features/healer-profile/domain/services/healer-slug";
import type { SearchBlogResult, SearchHealerResult } from "../../types";

const cardMotion = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as const } },
};

function initials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function HealerResultCard({
  href,
  healer,
  locale,
}: {
  href?: string;
  healer: SearchHealerResult;
  locale: AppLocale;
}) {
  const profileHref = href ?? localizeHref(locale, getPublicHealerProfilePath(healer));
  const bookHref = `${profileHref}/book`;
  const expertiseTags = [healer.category, ...healer.credentials.map((c) => c.title)].filter(
    (tag): tag is string => Boolean(tag),
  );

  return (
    <motion.div
      variants={cardMotion}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border/70 bg-card shadow-[0_8px_30px_rgba(0,0,0,0.015)] transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_18px_44px_rgba(21,90,125,0.10)]"
    >
      <Link
        href={profileHref}
        aria-label={
          locale === "hi"
            ? `${healer.fullName} की प्रोफ़ाइल देखें`
            : `View ${healer.fullName}'s profile`
        }
        className="flex flex-1 flex-col p-6 pb-4 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:outline-none"
      >
        <div className="flex items-start gap-5">
          <div className="relative h-32 w-32 shrink-0 overflow-hidden rounded-xl border border-border bg-primary/10 sm:h-36 sm:w-36">
            {healer.avatarUrl ? (
              <Image
                src={healer.avatarUrl}
                alt={healer.fullName}
                fill
                sizes="144px"
                className="object-cover"
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-3xl font-semibold text-primary">
                {initials(healer.fullName)}
              </span>
            )}
          </div>

          <div className="min-w-0 flex-1 pt-1">
            <div className="flex items-center gap-1.5">
              <h3 className="truncate font-heading text-xl font-semibold text-foreground group-hover:text-primary">
                {healer.fullName}
              </h3>
              {healer.verificationStatus === "VERIFIED" && (
                <BadgeCheck className="h-4 w-4 shrink-0 fill-primary text-white" />
              )}
            </div>
            {healer.experience != null && (
              <p className="mt-1.5 text-sm text-muted-foreground">
                {healer.experience}+ {locale === "hi" ? "वर्षों का अनुभव" : "years of experience"}
              </p>
            )}
            {[healer.city, healer.state].filter(Boolean).length > 0 && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin className="h-3 w-3" />
                {[healer.city, healer.state].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        </div>

        {expertiseTags.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span className="shrink-0 font-medium">
              {locale === "hi" ? "विशेषज्ञता:" : "Expertise:"}
            </span>
            {expertiseTags.slice(0, 3).map((tag) => (
              <span key={tag} className="rounded-md bg-muted px-2.5 py-1 text-foreground/80">
                {tag}
              </span>
            ))}
          </div>
        )}

        {healer.languages.length > 0 && (
          <p className="mt-3 text-xs text-muted-foreground">
            <span className="font-medium">{locale === "hi" ? "भाषाएँ:" : "Speaks:"}</span>{" "}
            {healer.languages.join(", ")}
          </p>
        )}

        {healer.bio && (
          <p className="mt-3 line-clamp-2 text-sm leading-relaxed font-light text-muted-foreground">
            {healer.bio}
          </p>
        )}

        {healer.personalized && (
          <span className="mt-3 flex w-fit items-center gap-1 text-[10px] font-semibold tracking-wide text-amber-600 uppercase">
            <Sparkles className="h-3 w-3" />
            {locale === "hi" ? "आपके लिए" : "For you"}
          </span>
        )}
      </Link>

      <div className="mt-auto flex items-center gap-2 border-t border-border/60 bg-muted/40 p-4">
        <Link
          href={profileHref}
          className="flex-1 rounded-full border border-border bg-card px-4 py-2.5 text-center text-xs font-semibold text-foreground transition hover:border-primary/40"
        >
          {locale === "hi" ? "प्रोफ़ाइल देखें" : "VIEW PROFILE"}
        </Link>
        <Link
          href={bookHref}
          className="flex flex-1 items-center justify-center gap-1 rounded-full bg-primary px-4 py-2.5 text-center text-xs font-semibold text-white transition hover:bg-primary/90"
        >
          {locale === "hi" ? "बुक करें" : "BOOK"}
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </motion.div>
  );
}

export function BlogResultCard({ blog, locale }: { blog: SearchBlogResult; locale: AppLocale }) {
  return (
    <motion.div variants={cardMotion}>
      <Link
        href={localizeHref(locale, `/blogs/${blog.slug}`)}
        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border/70 bg-card shadow-[0_8px_30px_rgba(0,0,0,0.015)] transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_18px_44px_rgba(21,90,125,0.10)]"
      >
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
          {blog.coverImageUrl ? (
            <Image
              src={blog.coverImageUrl}
              alt={blog.title}
              fill
              sizes="(max-width: 768px) 100vw, 360px"
              className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10" />
          )}
        </div>

        <div className="flex flex-1 flex-col p-5">
          <div className="mb-2 flex items-center gap-2 text-[10px] font-bold tracking-widest text-primary uppercase">
            {blog.category?.name ?? (locale === "hi" ? "सामान्य" : "General")}
          </div>
          <h3 className="line-clamp-2 font-heading text-lg leading-snug font-medium text-foreground transition-colors group-hover:text-primary">
            {blog.title}
          </h3>
          {blog.excerpt && (
            <p className="mt-2 line-clamp-2 text-sm leading-relaxed font-light text-muted-foreground">
              {blog.excerpt}
            </p>
          )}

          <div className="mt-auto flex items-center justify-between pt-4 text-xs text-muted-foreground">
            <span className="truncate font-medium text-foreground/80">{blog.author.name}</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {blog.readTime}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-1 border-t border-border/40 px-5 py-3 text-xs font-semibold text-primary">
          {locale === "hi" ? "पढ़ें" : "Read"}
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
        </div>
      </Link>
    </motion.div>
  );
}

export function ResultCardSkeleton() {
  return (
    <div className="h-80 animate-pulse rounded-2xl border border-border/60 bg-card p-6">
      <div className="flex gap-5">
        <div className="h-32 w-32 rounded-xl bg-muted sm:h-36 sm:w-36" />
        <div className="flex-1 space-y-2 pt-1">
          <div className="h-4 w-2/3 rounded bg-muted" />
          <div className="h-3 w-1/2 rounded bg-muted" />
        </div>
      </div>
      <div className="mt-5 space-y-2">
        <div className="h-3 w-full rounded bg-muted" />
        <div className="h-3 w-4/5 rounded bg-muted" />
      </div>
    </div>
  );
}
